import '@/assets/styles/main.scss';
import { getHostname, glsClearLocalStorageWhenAppVersionChange } from '@gls-its-frontends/common/gls-app-config';
import { glsInjectDynatrace } from '@gls-its-frontends/common/gls-dynatrace';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, Pinia } from 'pinia';
import { createPersistedState } from 'pinia-plugin-persistedstate';
import { type App as AppType, createApp, type Plugin } from 'vue';
import en from '@/assets/i18n/en.json';
import {
    DEV_ENVIRONMENT_CONFIG,
    PROD_ENVIRONMENT_CONFIG,
    SANDBOX_ENVIRONMENT_CONFIG,
    STAGE_ENVIRONMENT_CONFIG
} from '@/constants/environment-configs';
import { LOCAL_STORAGE_PREFIX } from '@/constants/local-storage-prefix';
import PrivilegeDirective from '@/directives/v-privilege';
import EnvironmentHostName from '@/enums/environment-hostname';
import { FeatureFlagService } from '@/services/feature-flag-service';
import { EnvironmentConfig } from '@/types/environment-config';

const APP_TITLE: string = 'ASA';
const STAGE_DYNATRACE_RUM_APP_ID: string = '99ecfff3baf0a190';
const PROD_DYNATRACE_RUM_APP_ID: string = 'c431750d08b161e';

const getAppTitle = (): string => {
    switch (getHostname()) {
        case EnvironmentHostName.PROD:
            return APP_TITLE;
        case EnvironmentHostName.LOCAL:
            return `${APP_TITLE} (LOCAL)`;
        case EnvironmentHostName.STAGE:
            return `${APP_TITLE} (STAGE)`;
        default:
            return `${APP_TITLE} (DEV)`;
    }
};

const setUpDynatrace = (): void => {
    switch (getHostname()) {
        case EnvironmentHostName.STAGE:
            glsInjectDynatrace(STAGE_DYNATRACE_RUM_APP_ID);
            break;
        case EnvironmentHostName.PROD:
            glsInjectDynatrace(PROD_DYNATRACE_RUM_APP_ID);
            break;
    }
};

const setEnvironmentConfig = (): void => {
    const redirectURI: string = `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? ':' + window.location.port : ''
    }`;

    const envData = {
        VITE_AZURE_AD_AUTHORITY: 'https://login.microsoftonline.com/e6170c30-202d-4926-b525-b8b882873f3b',
        VITE_MSAL_REDIRECT_URI: redirectURI,
        VITE_MSAL_POSTLOGOUT_REDIRECT_URI: `${redirectURI}/login`
    };

    window.env = { ...envData, ...getEnvConfig() };
};

const getEnvConfig = (): EnvironmentConfig => {
    switch (getHostname()) {
        case EnvironmentHostName.PROD:
            return PROD_ENVIRONMENT_CONFIG;
        case EnvironmentHostName.SANDBOX:
            return SANDBOX_ENVIRONMENT_CONFIG;
        case EnvironmentHostName.STAGE:
            return STAGE_ENVIRONMENT_CONFIG;
        default:
            return DEV_ENVIRONMENT_CONFIG;
    }
};

(async (): Promise<void> => {
    await FeatureFlagService.init();
    glsClearLocalStorageWhenAppVersionChange(APP_VERSION, LOCAL_STORAGE_PREFIX);
    setEnvironmentConfig();
    setUpDynatrace();
    document.title = getAppTitle();

    const { default: App } = await import('@/App.vue');
    const { default: router } = await import('@/routing/router');

    const app: AppType<Element> = createApp(App);

    const pinia: Pinia = createPinia().use(
        createPersistedState({
            key: (key: string): string => LOCAL_STORAGE_PREFIX + key
        })
    );
    const i18n: Plugin = glsCreateI18nInstance({ en });

    app.directive('privilege', PrivilegeDirective)
        .use(pinia)
        .use(i18n)
        .use(VueQueryPlugin, glsVueQueryPluginOptions)
        .use(router)
        .mount('#root');
})();
